import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container"];
  static values = { url: String };

  connect() {
    this._aborted = false;
    if (this.hasUrlValue && this.urlValue) {
      this.loadPdf();
    }
  }

  disconnect() {
    this._aborted = true;
  }

  async loadPdf() {
    this.containerTarget.innerHTML = this.loadingHtml();

    let doc;
    try {
      const pdfjsLib = await this.pdfjsLib();
      if (this._aborted) return;

      doc = await pdfjsLib.getDocument({ url: this.urlValue }).promise;
      if (this._aborted) return;

      this.containerTarget.innerHTML = "";

      await this.waitForWidth();
      if (this._aborted) return;

      for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
        if (this._aborted) break;
        const canvas = await this.renderPage(doc, pageNum);
        if (this._aborted) break;
        this.containerTarget.appendChild(canvas);
      }
    } catch (error) {
      if (!this._aborted) {
        console.error("Failed to load PDF:", error);
        this.containerTarget.innerHTML = this.errorHtml();
      }
    } finally {
      doc?.destroy();
    }
  }

  waitForWidth(maxFrames = 10) {
    return new Promise((resolve) => {
      let frames = 0;
      const check = () => {
        if (this._aborted || this.containerTarget.clientWidth > 0 || frames >= maxFrames) {
          resolve();
        } else {
          frames++;
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    });
  }

  async renderPage(doc, pageNum) {
    const page = await doc.getPage(pageNum);
    const containerWidth = this.containerTarget.clientWidth || this.element.clientWidth || 800;
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = containerWidth / baseViewport.width;
    const outputScale = window.devicePixelRatio || 1;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = "100%";
    canvas.style.display = "block";
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", `BDM certificate, page ${pageNum}`);

    const context = canvas.getContext("2d");
    const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

    await page.render({ canvasContext: context, transform, viewport }).promise;
    page.cleanup();

    return canvas;
  }

  async pdfjsLib() {
    const lib = await import("pdfjs-dist");
    if (!lib.GlobalWorkerOptions.workerSrc) {
      lib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
      ).toString();
    }
    return lib;
  }

  loadingHtml() {
    return `<div role="status" class="flex items-center justify-center py-16 text-gray-400">
      <svg aria-hidden="true" class="animate-spin size-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
      </svg>
      <span class="text-sm">Loading document&hellip;</span>
    </div>`;
  }

  errorHtml() {
    return `<div class="flex items-center justify-center py-16 text-red-400">
      <span class="text-sm">Failed to load document. Please download it instead.</span>
    </div>`;
  }
}
